import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import {
  Button,
  Checkbox,
  Col,
  DatePicker,
  Divider,
  Flex,
  Form,
  FormProps,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Spin,
  Typography
} from "antd";
import { addOrUpdateAccount } from "api/services/AccountService";
import { TAccount } from "api/services/models/types";
import { useTDAuthentication } from "auth/AuthenticationProvider";
import ErrorViewer from "components/ErrorViewer";
import { usePicklists } from "components/picklists/PicklistContextProvider";
import dayjs from "dayjs";
import { ReactComponent as AddIcon } from "Icons/vuesax/add.svg";
import { ReactComponent as CalendarIcon } from "Icons/vuesax/calendar.svg";
import { ReactComponent as TickSquareLinear } from "Icons/vuesax/tick-square.svg";
import { useEffect, useState } from "react";

const AddOrUpdateAccount = ({
  account,
  onDone,
  onCancel
}: {
  account?: TAccount;
  onDone: (account?: TAccount) => void;
  onCancel: () => void;
}) => {
  const { webDUser } = useTDAuthentication();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>();
  const { locations, refresh } = usePicklists();
  const isNew = account == null;
  const regionlength = account?.regions?.length ?? 0;

  const Header = ({
    account,
    onSubmit
  }: {
    account?: Partial<TAccount>;
    onSubmit: () => void;
  }) => (
    <Flex
      wrap
      gap="middle"
      align="end"
      justify="space-between"
      style={{ marginBottom: "16px" }}>
      <Typography.Title style={{ fontSize: "1rem" }} className="font-1212">
        {!account ? "Add Account" : "Manage Account"}
      </Typography.Title>
      <Space size="small" style={{ marginRight: "1rem" }}>
        <Button
          type="primary"
          htmlType="submit"
          className="default-button primary-btn"
          onClick={onSubmit}>
          <TickSquareLinear className="default-icon" />
          <span>Save</span>
        </Button>
        {isNew && (
          <Button
            style={{ border: "none" }}
            icon={
              <AddIcon
                className="close-icon"
                style={{ color: "#121212", verticalAlign: "initial", margin: "0 0 0 3rem" }}
              />
            }
            onClick={e => onCancel()}></Button>
        )}
      </Space>
    </Flex>
  );

  useEffect(() => {
    if (account) {
      form.setFieldsValue(account);
    } else {
      form.setFieldsValue({ regions: [{ regionId: "", regionName: "" }] });
    }
  }, [account]);

  const onSave = () => {
    form
      .validateFields()
      .then(() => form.submit())
      .catch(e => console.log(e));
  };
  const onFinish: FormProps<TAccount>["onFinish"] = values => {
    setLoading(true);
    setError(null);
    addOrUpdateAccount({ ...account, ...values }, account == null)
      .then(onDone)
      .then(() => refresh?.("Regions"))
      .catch(setError)
      .finally(() => setLoading(false));
  };

  return (
    <>
      <Header account={account} onSubmit={onSave}></Header>
      {loading && (
        <Flex justify="space-around" style={{ width: "100%", textAlign: "center" }}>
          <Spin tip="Saving, please wait..." />
        </Flex>
      )}
      {error && <ErrorViewer error={error} />}
      <Form
        form={form}
        labelAlign="left"
        name="add_or_update_account"
        onFinish={onFinish}
        initialValues={{
          tatInHours: 24,
          archiveAfter: 90,
          priority: 0,
          sensitivity: 0,
          enableFax: false,
          effectiveDate: dayjs().startOf("day").format("YYYY-MM-DD")
        }}
        layout="vertical"
        validateTrigger={["onChange", "onBlur"]}>
        <Row gutter={16}>
          <Col xs={24} sm={12} md={8} lg={8}>
            <Form.Item
              key={"accountId"}
              name={"accountId"}
              label={
                <label htmlFor="accountId" className="input-label">
                  Account Id
                </label>
              }
              rules={[{ required: true, min: 2, max: 20, message: "Please enter Account ID" }]}>
              <Input
                autoComplete="false"
                className="filter-input-transparent"
                aria-autocomplete="none"
                readOnly={account != null}
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={8} lg={8}>
            <Form.Item
              key={"parentAccountId"}
              name={"parentAccountId"}
              label={
                <label htmlFor="parentAccountId" className="input-label">
                  Parent Account Id
                </label>
              }>
              <Input
                autoComplete="false"
                className="filter-input-transparent"
                aria-autocomplete="none"
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={8} lg={8}>
            <Form.Item
              key={"accountName"}
              name={"accountName"}
              label={
                <label htmlFor="accountName" className="input-label">
                  Account Name
                </label>
              }
              rules={[
                { required: true, min: 3, max: 255, message: "Please enter Account Name" }
              ]}>
              <Input
                autoComplete="false"
                className="filter-input-transparent"
                aria-autocomplete="none"
              />
            </Form.Item>
          </Col>

          {!isNew && (
            <Col xs={24} sm={12} md={8} lg={8}>
              <Form.Item
                key={"primaryLocationId"}
                name={"primaryLocationId"}
                label={
                  <label htmlFor="primaryLocationId" className="input-label">
                    Primary Location
                  </label>
                }
                rules={[{ required: true }]}>
                <Select
                  className="input-field"
                  style={{ textAlign: "left" }}
                  options={locations}
                  variant="borderless"
                  fieldNames={{ label: "locationName", value: "locationId" }}
                />
              </Form.Item>
            </Col>
          )}
          <Col xs={24} sm={12} md={8} lg={8}>
            <Form.Item
              key={"effectiveDate"}
              name={"effectiveDate"}
              label={
                <label htmlFor="effectiveDate" className="input-label">
                  Effective Date
                </label>
              }
              getValueProps={(d: string) => ({ value: dayjs(d) })}>
              <DatePicker
                format="YYYY-MM-DD"
                placeholder="DD/MM/YYYY"
                allowClear={false}
                style={{ width: "100%" }}
                className="custom-datepicker-white"
                suffixIcon={<CalendarIcon className="calendar-icon" />}
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={8} lg={8}>
            <Form.Item
              key={"tatInHours"}
              name={"tatInHours"}
              label={
                <label htmlFor="tatInHours" className="input-label">
                  Turn Around Time (Hours)
                </label>
              }
              rules={[
                { required: true, message: "Please enter Turn Around Time" },
                {
                  validator: (_, value) => {
                    const number = Number(value);

                    if (number >= 2 && number <= 72) {
                      return Promise.resolve();
                    }

                    return Promise.reject(
                      new Error("Turn around time should be between 2 and 72")
                    );
                  },
                }
              ]}>
              <Input
                type="number"
                max={72}
                min={2}
                style={{ width: "100%" }}
                className="filter-input-transparent"
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={8} lg={8}>
            <Form.Item
              key={"archiveAfter"}
              name={"archiveAfter"}
              label={
                <label htmlFor="archiveAfter" className="input-label">
                  Archive After (days)
                </label>
              }
              rules={[{ required: true, message: "Please enter Archive After" }]}>
              <InputNumber
                min={0}
                style={{ width: "100%" }}
                className="filter-input-transparent"
              />
            </Form.Item>
          </Col>
        </Row>

        {webDUser?.isInternalUser() && (
          <>
            <Divider />
            <Row gutter={8}>
              <Col xs={24} sm={12} md={6} lg={6}>
                <Form.Item
                  key={"enableFax"}
                  name={"enableFax"}
                  rules={[{ required: true }]}
                  layout="horizontal"
                  valuePropName="checked"
                  style={{ textAlign: "left" }}>
                  <Checkbox>Enable Fax</Checkbox>
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={6} lg={6}>
                <Form.Item
                  key={"removeBlankMarkers"}
                  name={"removeBlankMarkers"}
                  valuePropName="checked"
                  layout="horizontal"
                  style={{ textAlign: "left" }}>
                  <Checkbox>Remove Blank Markers</Checkbox>
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={6} lg={6}>
                <Form.Item
                  key={"hasCorePatientVisits"}
                  name={"hasCorePatientVisits"}
                  layout="horizontal"
                  valuePropName="checked"
                  style={{ textAlign: "left" }}>
                  <Checkbox>Has Patient Visits</Checkbox>
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={6} lg={6}>
                <Form.Item
                  key={"sendFromIndia"}
                  name={"sendFromIndia"}
                  valuePropName="checked"
                  layout="horizontal"
                  style={{ textAlign: "left" }}>
                  <Checkbox>Send From India</Checkbox>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col xs={24} sm={12} md={6} lg={6}>
                <Form.Item
                  key={"priority"}
                  name={"priority"}
                  label={"Priority"}
                  rules={[
                    {
                      validator: (_, value) => {
                        const number = Number(value);

                        if (number >= 0 && number <= 10) {
                          return Promise.resolve();
                        }

                        return Promise.reject(
                          new Error("Priority should be between 0 and 10")
                        );
                      },
                    }
                  ]}
                >
                  <Input type="number" min={0} max={10} style={{ width: "100%" }} />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={6} lg={6}>
                <Form.Item
                  key={"sensitivity"}
                  name={"sensitivity"}
                  label={"Sensitivity"}
                  rules={[
                    {
                      validator: (_, value) => {
                        const number = Number(value);

                        if (number >= 0 && number <= 10) {
                          return Promise.resolve();
                        }

                        return Promise.reject(
                          new Error("Sensitivity should be between 0 and 10")
                        );
                      },
                    }
                  ]}
                  >
                  <Input type="number" min={0} max={10} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
            </Row>
          </>
        )}
        <Divider />
        <Form.List name="regions">
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name }, index) => (
                <Row key={key} gutter={16} align="middle">
                  <Col md={10}>
                    <Form.Item
                      name={[name, "regionId"]}
                      label="Region"
                      rules={[{ required: true, message: "Region is required!" }]}>
                      <Input
                        placeholder="Enter Region"
                        readOnly={!isNew ? index + 1 <= regionlength : false}
                        autoComplete="off"
                      />
                    </Form.Item>
                  </Col>
                  <Col md={10}>
                    <Form.Item
                      name={[name, "regionName"]}
                      label="Region Name"
                      rules={[{ required: true, message: "Region Name is required!" }]}>
                      <Input placeholder="Enter Region Name" autoComplete="off" />
                    </Form.Item>
                  </Col>
                  <Col md={isNew ? 2 : 1}>
                    {(isNew ? index !== 0 : index + 1 > regionlength) && (
                      <Button
                        danger
                        onClick={() => {
                          remove(name);
                        }}
                        icon={<DeleteOutlined />}
                      />
                    )}
                  </Col>
                  <Col md={isNew ? 2 : 1}>
                    {index === fields.length - 1 && (
                      <Button
                        type="primary"
                        onClick={() => {
                          add();
                        }}
                        icon={<PlusOutlined />}
                      />
                    )}
                  </Col>
                </Row>
              ))}
            </>
          )}
        </Form.List>
      </Form>
    </>
  );
};

export default AddOrUpdateAccount;
